// hooks/useMindmapDataCached.js - VERSION CORRIGÉE avec données centralisées
import { useCallback, useMemo, useState } from 'react';
import { useCacheManager } from './useCacheManager';

export const useMindmapDataCached = (data, hooks, options = {}) => {
    // Options de cache
    const {
        cacheDuration = 3 * 60 * 1000, // 3 minutes pour mindmap (plus courte car interactive)
    } = options;

    // NOUVEAU : Utiliser les données centralisées au lieu des hooks individuels
    const {
        objectifs = [],
        plans = [],
        isLoading = false,
        hasError = false,
        errors = {},
        isFromCache = false,
        cacheInfo = {}
    } = data || {};

    const {
        refreshObjectifs,
        refreshPlans,
        refreshAll,
        plansCachedHook
    } = hooks || {};

    // Récupérer updateTaskStatus depuis les hooks
    const updateTaskStatus = plansCachedHook?.updateTaskStatus || null;

    // Gestionnaire de cache
    const { getCacheKey, setCacheData, getCacheData, invalidateCache } = useCacheManager();

    // État pour gérer les tâches dépliées/repliées
    const [expandedTasks, setExpandedTasks] = useState(new Set());

    // Fonction de rafraîchissement centralisée optimisée
    const handleRefresh = useCallback(() => {
        console.log('🔄 Rafraîchissement Mindmap avec données centralisées');
        
        // Invalider le cache mindmap
        invalidateCache('mindmap');
        
        // Utiliser le rafraîchissement global centralisé si disponible
        if (refreshAll && typeof refreshAll === 'function') {
            refreshAll();
        } else {
            // Fallback sur les rafraîchissements individuels
            if (refreshObjectifs && typeof refreshObjectifs === 'function') {
                refreshObjectifs();
            }
            if (refreshPlans && typeof refreshPlans === 'function') {
                refreshPlans();
            }
        }
    }, [refreshAll, refreshObjectifs, refreshPlans, invalidateCache]);

    // Fonction pour gérer l'expand/collapse des tâches (UNE SEULE À LA FOIS)
    const toggleTaskExpansion = useCallback((taskId) => {
        setExpandedTasks(prev => {
            // Si la tâche cliquée est déjà ouverte, on la ferme
            if (prev.has(taskId)) {
                return new Set(); // Fermer tout
            } else {
                // Sinon, on ferme tout et on ouvre seulement celle-ci
                return new Set([taskId]);
            }
        });
    }, []);

    // Fonction pour fermer toutes les tâches sauf celle cliquée
    const handleNodeClick = useCallback((nodeId) => {
        if (nodeId.startsWith('task-')) {
            toggleTaskExpansion(nodeId);
        } else {
            // Si on clique sur autre chose (objectif, sous-tâche, background), on ferme TOUT
            setExpandedTasks(new Set());
        }
    }, [toggleTaskExpansion]);

    // Fonction pour mapper les statuts d'affichage
    const mapStatusDisplay = useCallback((status) => {
        const statusMapping = {
            'a_faire': 'planned',
            'en_cours': 'in_progress',
            'fait': 'completed',
            'annule': 'cancelled',
            'pending': 'planned',
            'in-progress': 'in_progress',
            'completed': 'completed',
            'cancelled': 'cancelled'
        };
        return statusMapping[status] || 'planned';
    }, []);

    // Fonction pour obtenir la couleur selon le statut
    const getStatusColor = useCallback((status) => {
        const colors = {
            'completed': { bg: '#dcfce7', border: '#22c55e', text: '#166534' },
            'in_progress': { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
            'planned': { bg: '#f3f4f6', border: '#9ca3af', text: '#6b7280' },
            'cancelled': { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' }
        };
        return colors[status] || colors.planned;
    }, []);

    // OPTIMISÉ : Transformer les données en format mindmap avec cache intelligent
    const mindmapData = useMemo(() => {
        // Créer une clé de cache basée sur les données d'entrée et l'état des tâches dépliées
        const cacheKey = getCacheKey('mindmap_data', {
            objectifsCount: objectifs.length,
            plansCount: plans.length,
            expandedTasks: Array.from(expandedTasks).sort(),
            objectifsHash: objectifs.map(obj => `${obj.id_objectif}-${obj.title}-${obj.status}`).join('|'),
            plansHash: plans.map(plan => `${plan.id_plan}-${plan.title}-${plan.id_objectif}`).join('|')
        });

        // Vérifier le cache d'abord
        const cachedData = getCacheData(cacheKey);
        if (cachedData) {
            console.log('📦 Données mindmap depuis le cache');
            return cachedData;
        }

        console.log('🔄 Calcul des données mindmap');
        
        const nodes = [];
        const edges = [];

        // COMPOSANTS ULTRA PETITS
        const OBJECTIF_WIDTH = 120;
        const OBJECTIF_HEIGHT = 50;
        const TASK_WIDTH = 90;
        const TASK_HEIGHT = 35;
        const SUBTASK_WIDTH = 70;
        const SUBTASK_HEIGHT = 25;

        // ESPACEMENT POUR GRILLE ALIGNÉE
        const HORIZONTAL_SPACING = 150;
        const OBJECTIF_ROW_Y = 0; // Tous les objectifs au niveau Y = 0
        const TASK_ROW_Y = 100;   // Toutes les tâches au niveau Y = 100
        const SUBTASK_ROW_Y = 180; // Toutes les sous-tâches au niveau Y = 180

        // ÉTAPE 1: Créer tous les objectifs sur la même ligne horizontale
        objectifs.forEach((objectif, objectifIndex) => {
            const objectifX = objectifIndex * HORIZONTAL_SPACING;
            const objectifY = OBJECTIF_ROW_Y;

            const mappedStatus = mapStatusDisplay(objectif.status || 'a_faire');
            const statusColor = getStatusColor(mappedStatus);

            const progressText = objectif.current_value && objectif.target_value
                ? ` (${objectif.current_value}/${objectif.target_value})`
                : '';

            // NŒUD OBJECTIF
            const objectifNode = {
                id: `objectif-${objectif.id_objectif}`,
                position: { x: objectifX, y: objectifY },
                data: {
                    label: `${objectif.title}${progressText}`,
                    originalData: objectif
                },
                category: objectif.category || 'Autre',
                status: mappedStatus,
                year: 'all',
                type: 'default',
                style: {
                    background: statusColor.bg,
                    border: `2px solid ${statusColor.border}`,
                    borderRadius: '4px',
                    padding: '4px 6px',
                    fontSize: '8px',
                    fontWeight: 'bold',
                    color: statusColor.text,
                    width: `${OBJECTIF_WIDTH}px`,
                    height: `${OBJECTIF_HEIGHT}px`,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    wordWrap: 'break-word',
                    lineHeight: '1.1'
                },
            };
            nodes.push(objectifNode);
        });

        // ÉTAPE 2: Collecter TOUTES les tâches de TOUS les objectifs
        const allTasks = [];
        
        objectifs.forEach((objectif) => {
            // CORRECTION: Essayer plusieurs méthodes de matching
            const relatedPlans = plans.filter(plan => 
                plan.id_objectif === objectif.id_objectif || 
                plan.id_objectif === objectif.id ||
                String(plan.id_objectif) === String(objectif.id_objectif)
            );

            relatedPlans.forEach(plan => {
                let tasks = [];
                if (plan.tasks) {
                    if (typeof plan.tasks === 'string') {
                        try {
                            tasks = JSON.parse(plan.tasks);
                        } catch (e) {
                            console.warn('❌ Erreur parsing tasks:', e);
                        }
                    } else if (Array.isArray(plan.tasks)) {
                        tasks = plan.tasks;
                    } else if (typeof plan.tasks === 'object' && plan.tasks !== null) {
                        // NOUVEAU: Si tasks est un objet, le convertir en array
                        tasks = Object.values(plan.tasks);
                    }
                }

                // VALIDATION: S'assurer que tasks est un array avec des éléments valides
                if (Array.isArray(tasks)) {
                    tasks = tasks.filter(task => task && typeof task === 'object' && task.title);
                } else {
                    tasks = [];
                }

                tasks.forEach((task, taskIndex) => {
                    const taskToAdd = {
                        ...task,
                        planId: plan.id_plan,
                        planTitle: plan.title,
                        taskIndex: taskIndex,
                        objectifId: objectif.id_objectif,
                        year: task.deadline ? new Date(task.deadline).getFullYear().toString() : 'no-date'
                    };
                    allTasks.push(taskToAdd);
                });
            });
        });

        // ÉTAPE 3: Placer TOUTES les tâches sur la même ligne horizontale
        allTasks.forEach((task, globalTaskIndex) => {
            const taskX = globalTaskIndex * HORIZONTAL_SPACING;
            const taskY = TASK_ROW_Y;

            const taskMappedStatus = mapStatusDisplay(task.status || 'a_faire');
            const taskStatusColor = getStatusColor(taskMappedStatus);

            const taskId = `task-${task.planId}-${task.taskIndex}`;
            const isExpanded = expandedTasks.has(taskId);
            const hasSubtasks = task.subtasks && Array.isArray(task.subtasks) && task.subtasks.length > 0;

            const taskLabel = hasSubtasks
                ? `${isExpanded ? '▼' : '▶'} ${task.title}`
                : task.title;

            // NŒUD TÂCHE
            const taskNode = {
                id: taskId,
                position: { x: taskX, y: taskY },
                data: {
                    label: taskLabel,
                    originalData: task,
                    hasSubtasks: hasSubtasks,
                    isExpanded: isExpanded
                },
                category: task.category || 'Autre',
                status: taskMappedStatus,
                year: task.year,
                type: 'default',
                style: {
                    background: taskStatusColor.bg,
                    border: `1px solid ${taskStatusColor.border}`,
                    borderRadius: '3px',
                    padding: '3px 4px',
                    fontSize: '7px',
                    fontWeight: '600',
                    color: taskStatusColor.text,
                    width: `${TASK_WIDTH}px`,
                    height: `${TASK_HEIGHT}px`,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: hasSubtasks ? 'pointer' : 'default',
                    lineHeight: '1.1'
                },
            };
            nodes.push(taskNode);

            // CONNEXION OBJECTIF → TÂCHE
            edges.push({
                id: `edge-${task.objectifId}-task-${task.planId}-${task.taskIndex}`,
                source: `objectif-${task.objectifId}`,
                target: taskId,
                type: 'smoothstep',
                style: {
                    stroke: taskStatusColor.border,
                    strokeWidth: 2
                }
            });

            // ÉTAPE 4: SOUS-TÂCHES sur la ligne du bas si dépliées
            if (hasSubtasks && isExpanded) {
                task.subtasks.forEach((subtask, subtaskIndex) => {
                    // Calculer position pour répartir les sous-tâches de cette tâche
                    const subtaskX = taskX + (subtaskIndex - (task.subtasks.length - 1) / 2) * (SUBTASK_WIDTH + 10);
                    const subtaskY = SUBTASK_ROW_Y;

                    const subtaskMappedStatus = mapStatusDisplay(subtask.status || 'a_faire');
                    const subtaskStatusColor = getStatusColor(subtaskMappedStatus);

                    // NŒUD SOUS-TÂCHE
                    const subtaskNode = {
                        id: `subtask-${task.planId}-${task.taskIndex}-${subtaskIndex}`,
                        position: { x: subtaskX, y: subtaskY },
                        data: {
                            label: subtask.title,
                            originalData: {
                                ...subtask,
                                planId: task.planId,
                                planTitle: task.planTitle,
                                taskIndex: task.taskIndex,
                                subtaskIndex: subtaskIndex,
                                isSubtask: true,
                                parentTaskTitle: task.title
                            }
                        },
                        category: task.category || 'Autre',
                        status: subtaskMappedStatus,
                        year: subtask.deadline ? new Date(subtask.deadline).getFullYear().toString() : 'no-date',
                        type: 'default',
                        style: {
                            background: subtaskStatusColor.bg,
                            border: `1px solid ${subtaskStatusColor.border}`,
                            borderRadius: '2px',
                            padding: '2px 3px',
                            fontSize: '6px',
                            color: subtaskStatusColor.text,
                            width: `${SUBTASK_WIDTH}px`,
                            height: `${SUBTASK_HEIGHT}px`,
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1.1'
                        },
                    };
                    nodes.push(subtaskNode);

                    // CONNEXION TÂCHE → SOUS-TÂCHE
                    edges.push({
                        id: `edge-task-${task.planId}-${task.taskIndex}-subtask-${subtaskIndex}`,
                        source: taskId,
                        target: `subtask-${task.planId}-${task.taskIndex}-${subtaskIndex}`,
                        type: 'smoothstep',
                        style: {
                            stroke: subtaskStatusColor.border,
                            strokeWidth: 1,
                            strokeDasharray: '4,4'
                        }
                    });
                });
            }
        });

        const result = { nodes, edges };
        
        // Mettre en cache le résultat (cache plus court pour les données interactives)
        setCacheData(cacheKey, result, cacheDuration);
        
        return result;
    }, [objectifs, plans, expandedTasks, mapStatusDisplay, getStatusColor, getCacheKey, getCacheData, setCacheData, cacheDuration]);

    // Extraction des données pour les filtres avec cache
    const availableYears = useMemo(() => {
        const cacheKey = getCacheKey('mindmap_years', { nodesLength: mindmapData.nodes.length });
        const cachedYears = getCacheData(cacheKey);
        
        if (cachedYears) {
            return cachedYears;
        }

        const years = mindmapData.nodes
            .filter(node => node.year && node.year !== 'all' && node.year !== 'no-date')
            .map(node => node.year);
        
        const result = [...new Set(years)].sort();
        setCacheData(cacheKey, result, 10 * 60 * 1000); // 10 minutes
        
        return result;
    }, [mindmapData.nodes, getCacheKey, getCacheData, setCacheData]);

    const availableCategories = useMemo(() => {
        const cacheKey = getCacheKey('mindmap_categories', { nodesLength: mindmapData.nodes.length });
        const cachedCategories = getCacheData(cacheKey);
        
        if (cachedCategories) {
            return cachedCategories;
        }

        const categories = mindmapData.nodes
            .filter(node => node.category && node.category !== 'Autre')
            .map(node => node.category);
        
        const result = [...new Set(categories)].sort();
        setCacheData(cacheKey, result, 10 * 60 * 1000); // 10 minutes
        
        return result;
    }, [mindmapData.nodes, getCacheKey, getCacheData, setCacheData]);

    const availableStatuses = useMemo(() => {
        const cacheKey = getCacheKey('mindmap_statuses', { nodesLength: mindmapData.nodes.length });
        const cachedStatuses = getCacheData(cacheKey);
        
        if (cachedStatuses) {
            return cachedStatuses;
        }

        const statuses = mindmapData.nodes
            .map(node => node.status)
            .filter(Boolean);
        
        const result = [...new Set(statuses)].sort();
        setCacheData(cacheKey, result, 10 * 60 * 1000); // 10 minutes
        
        return result;
    }, [mindmapData.nodes, getCacheKey, getCacheData, setCacheData]);

    // Fonction pour mettre à jour le statut d'une tâche avec invalidation du cache
    const updateTaskStatusWithCache = useCallback(async (taskData, newStatus) => {
        try {
            // Invalider le cache avant la mise à jour
            invalidateCache('mindmap');
            
            // Décomposer les données de la tâche pour appeler updateTaskStatus
            const { planId, taskIndex, subtaskIndex } = taskData;
            
            // Appeler la fonction de mise à jour avec les bons paramètres
            if (updateTaskStatus) {
                const result = await updateTaskStatus(planId, taskIndex, newStatus, subtaskIndex);
                
                // Invalider à nouveau après la mise à jour pour forcer le rechargement
                invalidateCache('mindmap');
                
                console.log('✅ Statut mis à jour et cache invalidé');
                return result;
            }
            
            return { success: false, error: 'Fonction updateTaskStatus non disponible' };
        } catch (error) {
            console.error('❌ Erreur mise à jour statut:', error);
            return { success: false, error: error.message };
        }
    }, [updateTaskStatus, invalidateCache]);

    return {
        // Données principales
        mindmapData,
        
        // Options de filtres
        availableYears,
        availableCategories,
        availableStatuses,
        
        // États centralisés
        isLoading,
        hasError,
        error: errors.objectifs || errors.plans,
        
        // Actions
        updateTaskStatus: updateTaskStatusWithCache,
        handleRefresh,
        handleNodeClick,
        
        // Utilitaires
        mapStatusDisplay,
        
        // NOUVEAU : Informations sur le cache
        isFromCache,
        expandedTasks,
        
        // Statistiques additionnelles
        stats: {
            objectifsCount: objectifs.length,
            plansCount: plans.length,
            totalNodes: mindmapData.nodes.length,
            totalEdges: mindmapData.edges.length,
            expandedTasksCount: expandedTasks.size
        }
    };
};